import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { offersAPI } from '../../utils/api';
import { formatCurrency, STATUS_COLORS, STATUS_LABELS } from '../../utils/constants';
import { Loader2, FileText, Plus } from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import ClientDocumentsTab from './ClientDocumentsTab';

interface ClientOffersTabProps {
  clientId: string;
}

export default function ClientOffersTab({ clientId }: ClientOffersTabProps) {
  const navigate = useNavigate();
  const { tenant } = useAuth();
  const { hasPermission } = usePermissions();
  const [history, setHistory] = useState(false);
  const [page, setPage] = useState(1);
  const [statoFilter, setStatoFilter] = useState<string>('');

  const { data: offersData, isLoading, isError } = useQuery({
    queryKey: ['client-offers', tenant?.id, clientId, page, statoFilter],
    queryFn: async () => {
      const res = await offersAPI.getOffers({
        cliente_id: clientId,
        page,
        limit: 10,
        stato: statoFilter || undefined,
      });
      return res.data;
    },
  });

  const offers = offersData?.data || [];
  const pagination = offersData?.pagination;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (isError) return <p role="alert" className="p-4 text-red-700">Impossibile caricare le offerte del cliente.</p>;

  return (
    <div className="space-y-4">
      {/* Filter */}
      <div className="flex items-center justify-between">
        <select
          value={statoFilter}
          onChange={(e) => {
            setStatoFilter(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">Tutti gli stati</option>
          {Object.entries(STATUS_LABELS).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
        {hasPermission('offerte:create') && <button className="rounded-md bg-primary-600 px-3 py-2 text-sm text-white" onClick={() => navigate(`/offers/new?cliente_id=${clientId}&returnTo=${encodeURIComponent(`/clients/${clientId}?tab=offerte`)}`)}>Nuova offerta</button>}
      </div>

      {offers.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 mb-4">Nessuna offerta per questo cliente</p>
          {hasPermission('offerte:create') && <button
            onClick={() => navigate(`/offers/new?cliente_id=${clientId}&returnTo=${encodeURIComponent(`/clients/${clientId}?tab=offerte`)}`)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Crea Offerta
          </button>}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Numero
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Titolo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Stato
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Importo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Scadenza
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Creata il
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {offers.map((offer) => (
                  <tr
                    key={offer.id}
                    onClick={() => navigate(`/offers/${offer.id}/edit?returnTo=${encodeURIComponent(`/clients/${clientId}?tab=offerte`)}`)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {offer.numero}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 max-w-[200px] truncate">
                      {offer.titolo}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: `${STATUS_COLORS[offer.stato]}20`,
                          color: STATUS_COLORS[offer.stato],
                        }}
                      >
                        {STATUS_LABELS[offer.stato] || offer.stato}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                      {formatCurrency(offer.importo_totale)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {offer.scadenza
                        ? new Date(offer.scadenza).toLocaleDateString('it-IT')
                        : '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {new Date(offer.created_at).toLocaleDateString('it-IT')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <div className="text-sm text-gray-700">
                Pagina {pagination.page} di {pagination.pages} ({pagination.total} offerte)
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50"
                >
                  Precedente
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= pagination.pages}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50"
                >
                  Successivo
                </button>
              </div>
            </div>
          )}
        </>
      )}
      <button className="text-sm text-primary-700 hover:underline" aria-expanded={history} onClick={() => setHistory(!history)}>Offerte storiche importate da altri gestionali</button>
      {history && <ClientDocumentsTab clientId={clientId} kind="offerta" />}
    </div>
  );
}
